import { debounce } from '~/helpers/debounce'

interface Response {
  error?: any,
  options: any,
  request: any,
  response: any
}

export const useCartStore = defineStore('cart', () => {
  const products = ref<Array<any>>([])
  const freeItems = ref<Array<any>>([])
  const selectedFreeItem = ref(null)
  const discount = ref({})
  const loading = ref()
  const summary = ref<Array<any>>([])
  const rawSummary = ref<Array<any>>([])
  const show = ref(false) // Shows mini cart
  const showMiniCartTimeout = ref(null) // Timeout for showing mini cart
  const shopToken = ref<string | null>(null)
  const shippingMethod = ref(null) // Can override shippingMethods in next step
  const predictedAddress = ref({})
  const cartId = ref(null)
  const showMergeList = ref(false)
  const oldProducts = ref<Array<object>>([])
  const forgetProducts = ref([])
  const { showToast } = useToast()
  const router = useRouter()
  const authStore = useAuthStore()
  const { $lang, $api } = useNuxtApp()

  const getOldProducts = async (response: Response) => {
    oldProducts.value = []
    await response.response._data.items.forEach((item:object) => {
      oldProducts.value.push(item)
    })
    showMergeList.value = true

    await router.push({ name: $lang + '-cart' })
  }

  const retrieveCartId = async () => {
    await $api('/cart/products', {
      method: 'GET',
      onResponse: (response:Response) => {
        if (response.response.status === 201) {
          cartId.value = response.response._data.CartId ? response.response._data.CartId : null
        }
      }
    })
  }

  const sync = debounce(async (cartOverview?: any) => {
    if (!cartId.value) {
      if (authStore.loggedIn) {
        await retrieveCartId()
      }
      return
    }

    let resetCart = true
    const { items } : { items: Product[]} = await $api('/cart/products',
      {
        method: 'GET',
        params: {
          showDetails: true
        },
        onResponse: async (response:Response) => {
          if (response.response.status === 207) {
            resetCart = false
            await getOldProducts(response)
          }
          if (response.response.status === 201) {
            resetCart = false
            cartId.value = response.response._data.CartId ? response.response._data.CartId : null
          }
          if (response.response.status === 200) {
            showMergeList.value = false
          }
        }
      }) ?? { items: [] }

    if (!resetCart) {
      if (cartOverview && oldProducts.value.length > 0) {
        showMergeList.value = true
      }
      return
    }
    if (showMergeList.value) { return }
    products.value = []
    if (items) {
      items?.forEach((product: Product) => {
        products.value.push({
          quantity: product.quantity,
          product: product.product,
          cId: product.cId,
          pId: product.pId,
          price: product.product?.price,
          salePercent: product.product?.salePercent,
          originalPrice: product.product?.originalPrice,
          size: product.product?.sizes.filter(size => size.pId === product.pId)[0].size,
          related: product.product?.related
        })
      })
    }
    return items
  }, 500, true)

  const softSync = async (cartOverview?: any) => {
    if (!authStore.loggedIn) {
      showMergeList.value = false
    }
    if (!cartId.value) {
      if (authStore.loggedIn) {
        await retrieveCartId()
      }
      return
    }
    let resetCart = true
    const { items }: { items: Product[] } = await $api('/cart/products',
      {
        method: 'GET',
        onResponse: (response:Response) => {
          if (response.response.status === 207) {
            resetCart = false
            getOldProducts(response)
          }
          if (response.response.status === 201) {
            cartId.value = response.response._data.CartId ? response.response._data.CartId : null
            sync(false)
          }
        }
      }) ?? { items: [] }

    if (!resetCart) {
      if (cartOverview) {
        showMergeList.value = true
        router.push({ name: $lang + '-cart' })
      }
      return
    }

    if (items) {
      items.forEach((product: Product) => {
        const existingItem = products.value.indexOf(products.value.find(row => { return row.pId === product.pId }))
        if (existingItem === -1) {
          products.value.push(product)
        }
        if (products.value[existingItem]) {
          products.value[existingItem].quantity = product.quantity
          products.value[existingItem].originalPrice = product.originalPrice
          products.value[existingItem].price = product.price
          products.value[existingItem].salePercent = product.salePercent
        }
      })
    }
    return items
  }

  const setErrorMessage = (errorProduct: Product) => {
    products.value[products.value.indexOf(products.value.find(product => { return errorProduct.pId === product.pId }))].errorMsg = errorProduct?.errors?.[0]?.message
  }

  const add = async (payload: any) => {
    let isInStock = true
    loading.value = true
    const existingItem = products.value.find(row => {
      return (
        row.cId === payload.product.cId &&
        row.pId === payload.size.pId
      )
    })

    const product = {
      quantity: existingItem?.quantity + 1 || 1,
      product: payload.product,
      cId: payload.product.cId,
      pId: payload.size.pId,
      price: payload.product.price,
      salePercent: payload.product.salePercent,
      originalPrice: payload.product.originalPrice,
      size: payload.size.size,
      related: payload.product.related
    }

    if (!existingItem) {
      products.value.push(product)
    }

    await $api('/cart/products', {
      method: 'POST',
      body: {
        items: [product]
      },
      onResponse: (response: Response) => {
        if (response.response.status === 201) {
          cartId.value = response.response._data.CartId ? response.response._data.CartId : null
          sync(false)
        }
        if (response.response.status === 207) {
          getOldProducts(response)
        }
        if (response.response.status === 400) {
          if (response.response?._data?.items?.[0]?.errors?.length) {
            showToast({ type: 'error', message: `${response.response?._data?.items?.[0]?.errors?.[0]?.message}` })
          }
          isInStock = false
        }
        if (response.response.status === 200) {
          sync(false)
        }
      }
    })

    if (existingItem && isInStock) {
      existingItem.quantity++
    }
    debouncedUpdate({ disableShipping: true })
  }

  const remove = async (payload: any) => {
    loading.value = true
    await $api('/cart/products',
      {
        method: 'DELETE',
        body: {
          pId: payload.pId
        },
        onResponse: (response:Response) => {
          if (response.response.status === 207) {
            getOldProducts(response)
          }
          if (response.response.status === 201) {
            cartId.value = response.response._data.CartId ? response.response._data.CartId : null
            sync(false)
          }
        }
      }
    )

    products.value = products.value.filter((product: Product) => {
      return product.pId !== payload.pId
    })

    debouncedUpdate({ disableShipping: true })
  }

  const changePrice = ({ product, newPrice }: { product: any, newPrice:any }) => {
    const existingItem = products.value.find((row: any) => {
      return (
        row.product?.cId === product.product?.cId && row.size.pId === product.size.pId
      )
    })

    if (existingItem) {
      existingItem.price = newPrice
      existingItem.product.price = newPrice
    }
    return existingItem
  }

  const changeOriginalPrice = ({ product, newPrice } : { product: any, newPrice: any }) => {
    const existingItem = products.value.find((row: any) => {
      return (
        row.product?.cId === product.product?.cId && row.size.pId === product.size.pId
      )
    })
    if (existingItem) {
      existingItem.originalPrice = newPrice
      existingItem.product.originalPrice = newPrice
    }
    return existingItem
  }

  const changeSale = ({ product, newSale } : { product: any, newSale: any }) => {
    const existingItem = products.value.find((row:any) => {
      return (
        row.product?.cId === product.product?.cId && row.size.pId === product.size.pId
      )
    })
    if (existingItem) {
      existingItem.salePercent = newSale
      existingItem.product.salePercent = newSale
    }
    return existingItem
  }

  const changeQuantity = async ({ cartProduct, value, errorMsg }: {cartProduct: any, value:number, errorMsg: any}) => {
    loading.value = true
    let exceededStock = false
    const existingItem = products.value.find((row: any) => {
      return (
        cartProduct.pId === row.pId
      )
    })

    if (existingItem) {
      existingItem.quantity = value > 0 ? value : 1
    }

    existingItem.errorMsg = ''
    if (existingItem && errorMsg) {
      existingItem.errorMsg = errorMsg
    }

    await $api('/cart/products', {
      method: 'POST',
      body: {
        items: [
          {
            quantity: existingItem?.quantity,
            product: cartProduct.product,
            cId: cartProduct.product?.cId,
            pId: cartProduct.pId,
            price: cartProduct.product?.price,
            salePercent: cartProduct.product?.salePercent,
            originalPrice: cartProduct.product?.originalPrice,
            size: cartProduct.size,
            related: cartProduct.product?.related

          }
        ]
      },
      onResponse: (response:Response) => {
        if (response.response.status === 201) {
          cartId.value = response.response._data.CartId ? response.response._data.CartId : null
          sync(false)
        }
        if (response.response.status === 207) {
          getOldProducts(response)
        }
        if (response.response.status === 400) {
          existingItem.quantity = response.response?._data?.items?.[0]?.quantity
          exceededStock = true
          if (response.response?._data?.items?.[0]?.errors?.length) {
            showToast({ type: 'error', message: `${response.response?._data?.items?.[0]?.errors?.[0]?.message}` })
          }
          setErrorMessage(response.response?._data?.items?.[0])
        }
      }
    })

    if (exceededStock) {
      await $api('/cart/products', {
        method: 'POST',
        body: {
          items: [
            {
              quantity: existingItem?.quantity,
              product: cartProduct.product,
              cId: cartProduct.product?.cId,
              pId: cartProduct.pId,
              price: cartProduct.product?.price,
              size: cartProduct.size
            }
          ]
        },
        onResponse: (response:Response) => {
          if (response.response.status === 201) {
            cartId.value = response.response._data.CartId ? response.response._data.CartId : null
            sync(false)
          }
          if (response.response.status === 207) {
            getOldProducts(response)
          }
          if (response.response.status === 400) {
            existingItem.quantity = response.response?._data?.items?.[0]?.quantity
            exceededStock = true
            if (response.response?._data?.items?.[0]?.errors?.length) {
              showToast({ type: 'error', message: `${response.response?._data?.items?.[0]?.errors?.[0]?.message}` })
            }
            setErrorMessage(response.response?._data?.items?.[0])
          }
        }
      })
    }

    debouncedUpdate({ disableShipping: true })
    return existingItem
  }

  const fixErrors = async ({ data = {}, silent = true } : {data: any, silent: Boolean}) => {
    if (!data?.products && !data?.coupons) { return false }
    const { $t } = useNuxtApp()
    const { showAlert } = useDialog()

    if (!data.errors) {
      return await Promise.resolve()
    }

    // @TODO: Make seperate modal for coupon only valid in app

    if (data.errors?.length) {
      data.errors.forEach((error: any) => {
        if (
          error.type === 'CouponNotFoundException' ||
          error.type === 'CouponCombinationException' ||
          error.type === 'CouponOnlyValidInAppException'
        ) {
          showAlert(error.message).finally(async () => {
            const paymentStore = usePaymentStore()
            await paymentStore.resetCoupon()
            await update({ silent: true })
          }).catch(() => {})
        }
        if (error.type === 'InsufficientStockException' || error.type === 'InvalidQuantityException') {
          const editedProduct = products.value.find(row => row.pId === error.id)
          changeQuantity({
            errorMsg: error.message,
            cartProduct: editedProduct,
            value: parseInt(error.correct)
          })
        }
        if (error.type === 'InvalidQuantityException') {
          const editedProduct = products.value.find(row => row.pId === error.id)
          changeQuantity({
            errorMsg: error.message,
            cartProduct: editedProduct,
            value: 1
          })
        }
        if (error.type === 'ProductOutOfStockException') {
          const editedProduct = products.value.find(row => row.pId === error.id)

          showAlert(`<b>${editedProduct.product?.title} ${editedProduct.product?.colorName}</b> ${$t('checkout/is-not-in-stock-anymore')}`).finally(() => {
            remove(editedProduct)
          }).catch(() => {})
        }
        if (error.type === 'DiscountCodeChangedException') {
          const paymentStore = usePaymentStore()
          paymentStore.resetCoupon()
        }

        if (error.type === 'SalePercentChangeException' || error.type === 'InvalidSalePercentException') {
          const editedProduct = products.value.find(row => row.pId === error.id)
          showToast({ message: (editedProduct?.product?.title ? `<b>${editedProduct.product?.title}</b><br />` : '') + error.message })
          changeSale({
            product: editedProduct,
            newSale: error.correct
          })
        }

        // Price in localstorage is different from cart-update
        if (error.type === 'PriceChangeException') {
          const editedProduct = products.value.find(row => row.pId === error.id)

          changePrice({
            product: editedProduct,
            newPrice: error.correct
          })
          if (!silent) {
            showToast({ message: error.message })
          }
        }
        if (error.type === 'OriginalPriceChangeException') {
          const editedProduct = products.value.find(row => row.pId === error.id)
          changeOriginalPrice({
            product: editedProduct,
            newPrice: error.correct
          })
        }

        if (error.type === 'ProductUnavailableException') {
          const productToDelete = products.value.find(row => row.pId === error.id)
          const message = productToDelete?.product?.title ? `<b>${productToDelete.product.title} ${productToDelete.product.colorName}</b> ${$t('checkout/does-not-exists-anymore')}` : error.message
          if (productToDelete) {
            showAlert(message).finally(() => {
              remove(productToDelete)
            }).catch(() => {})
          }
        }
      })
    }
    return await Promise.resolve()
  }

  const update = async ({ silent = true, disableAuthCheck = false, disableShipping = false }) => {
    const paymentStore = usePaymentStore()
    const shippingStore = useShippingStore()

    if (products.value.length < 1 || !cartId.value || showMergeList.value) { return }
    loading.value = true

    /* eslint-disable prefer-const */
    let headers:any = {}
    if (shopToken.value) {
      headers.ShopToken = shopToken.value
    }

    const cartContent:any = {
      items: products.value.map(product => {
        return {
          pId: product.pId,
          quantity: product.quantity,
          cId: product.cId,
          price: product.price,
          salePercent: product.salePercent,
          originalPrice: product.originalPrice
        }
      }),
      shipping: !disableShipping
        ? {
            method: shippingStore.method,
            address: shippingStore.address,
            expectedDelivery: shippingStore.expectedDelivery
          }
        : null,
      coupons: paymentStore.coupons,
      email: shippingStore.address?.email,
      freeItem: {
        selected: selectedFreeItem.value
      }
    }

    if (paymentStore.options || paymentStore.selected?.method) {
      cartContent.payment = {
        method: paymentStore.options || paymentStore.selected?.method || null,
        phone: paymentStore.requirements.phone || null,
        birthday: paymentStore.requirements.birthday || null,
        acceptconditions: paymentStore.requirements.acceptconditions || null
      }
    }

    const data: CartUpdateResponse = await $api('cart', {
      key: 'cart',
      body: JSON.parse(JSON.stringify(cartContent)),
      method: 'POST',
      progress: false,
      headers,
      disableAuthCheck,
      onResponse: (response:Response) => {
        if (response.response.status === 201) {
          cartId.value = response.response._data.CartId ? response.response._data.CartId : null
          sync(false)
        }
        if (response.response.status === 207) {
          getOldProducts(response)
        }
        if (response.response.status === 400) {
          if (response.response?._data?.items?.[0]?.errors?.length) {
            showToast({ type: 'error', message: `${response.response?._data?.items?.[0]?.errors?.[0]?.message}` })
          }
          if (response.response?._data?.items?.length) {
            setErrorMessage(response.response?._data?.items?.[0])
          }
        }
      }
    })

    loading.value = false

    // Set default shipping address
    if (data.shipping?.address) {
      predictedAddress.value = data.shipping.address
    }
    if (data.formattedSummary) {
      summary.value = data?.formattedSummary
    }
    if (data?.summary) {
      rawSummary.value = data?.summary
    }
    if (data?.shipping && data?.shipping.method) {
      shippingMethod.value = data?.shipping.method
    }
    if (data?.freeItem) {
      freeItems.value = data?.freeItem
    }
    if (data && !data.freeItem) {
      freeItems.value = []
      selectedFreeItem.value = null
    }

    fixErrors({ data, silent })

    return { data }
  }

  const debouncedUpdate = debounce(async function (args: any) {
    await update(args)
    loading.value = false
  }, 300)

  const empty = async () => {
    await Promise.all(products.value.map(async (product: Product) => {
      await remove(product)
    }))
  }

  const reset = () => {
    showMergeList.value = false
    products.value = []
    rawSummary.value = []
    summary.value = []
    oldProducts.value = []
    selectedFreeItem.value = null
  }

  const total = computed(() => {
    const total = products.value.reduce((acc, product) => {
      return (
        acc +
        parseFloat(product?.price?.replace(',', '.')) *
          parseFloat(product.quantity)
      )
    }, 0)

    return total.toFixed(2)
  })

  const mergeOldProducts = async (mergeProducts:Boolean) => {
    const merge = forgetProducts.value.length === oldProducts.value.length ? false : mergeProducts
    const itemsToRemove = forgetProducts.value.map((pId: number) => ({ pId }))
    await $api('/cart/merge',
      {
        method: 'POST',
        body: {
          merge,
          itemsToRemove
        },
        onResponse: (response:Response) => {
          cartId.value = response.response._data.CartId
        }
      })
    showMergeList.value = false
    forgetProducts.value = []
    oldProducts.value = []
    update({ disableShipping: true })
    sync(false)
  }

  const cartAmount = computed(() => {
    return products.value.reduce((total:number, product:Product) => {
      return total + product.quantity
    }, 0)
  })

  return {
    cartId,
    products,
    freeItems,
    selectedFreeItem,
    discount,
    loading,
    summary,
    rawSummary,
    show,
    showMiniCartTimeout,
    shopToken,
    shippingMethod,
    predictedAddress,
    total,
    showMergeList,
    forgetProducts,
    oldProducts,
    cartAmount,
    debouncedUpdate,
    add,
    remove,
    mergeOldProducts,
    changeQuantity,
    fixErrors,
    update,
    softSync,
    sync,
    retrieveCartId,
    reset,
    empty
  }
}, {
  persistedState: {
    persist: true
  }
})
